<template>
  <div class="w-full h-96 flex justify-center items-center">
    <NuxtImg class="w-20" src="/icons/loading.svg" />
  </div>
</template>

<script setup lang="ts">
import type { GetStartedService } from "../../types";
import { createStaleResponseGuard } from "~/utils/staleResponseGuard";

const route = useRoute();
const getStartedModalStore = useGetStartedModalStore();
const mapStore = useMapStore();

/**
 * Selected service entity.
 */
const selectedService = computed(() => getStartedModalStore.selectedService);

/**
 * Get clinic slug from route query params.
 */
const selectedClinicSlug = computed(() => route.query.slug);

/**
 * Lets a slow response for an earlier clinic be ignored once another clinic is selected,
 * or this component unmounts.
 */
const clinicRequests = createStaleResponseGuard();

/**
 * Fetch clinic detail by selected slug.
 */
async function fetchClinicDetailBySlug() {
  // A cleared slug deliberately doesn't invalidate the in-flight request: browser Back leaves the
  // modal on the tests step, so cancelling here would leave the panel on the loader for good.
  if (!selectedClinicSlug.value) return;
  const requestId = clinicRequests.start();

  // Start loading before the clinic detail fetch, so the tests panel never shows the previous
  // clinic's tests or "no tests available" while this clinic is being looked up.
  mapStore.startPanelTestsLoading();
  try {
    const { data: clinicDetail } = await useFetchClinicDetailBySlug(selectedClinicSlug.value);
    const clinic = clinicDetail.value?.data;
    if (!clinic || !clinicRequests.isCurrent(requestId)) return;

    mapStore.setSelectedClinic({
      id: clinic.id,
      title: clinic.display_title,
      slug: clinic.slug,
    });
    await fetchClinicTests(clinic.id, requestId);
  } finally {
    if (clinicRequests.isCurrent(requestId)) {
      mapStore.setPanelTestsLoading(false);
    }
  }
}

/**
 * Fetch all tests related to a specific clinic.
 * Get started → clinic test should show both `clinic-test` and `instant-test` products,
 * but `instant-test` is only requested when that service is enabled site-wide so
 * deployments without it keep their previous behaviour.
 * @param clinic - Clinic Id from where we should fetch tests.
 * @param requestId - Id from `clinicRequests`; the result is dropped if it is no longer current.
 */
const fetchClinicTests = async (clinic: string, requestId: number) => {
  const { data: servicesResult } = await useServiceListType();
  const services: GetStartedService[] = servicesResult.value?.data ?? [];
  const isInstantTestEnabled = services.some((service) => service.slug === "instant-test");

  const params: Record<string, string> = {
    clinic,
    "service_type[0]": "clinic-test",
  };
  if (isInstantTestEnabled) {
    params["service_type[1]"] = "instant-test";
  }

  const { data: productsList } = await useFetchProductList(params);
  if (!clinicRequests.isCurrent(requestId)) return;
  mapStore.getTests(productsList.value?.data ?? []);
};

/**
 * Watch selected clinic slug change in order to update the products.
 */
watch(selectedClinicSlug, fetchClinicDetailBySlug);

/**
 * Drop any in-flight response when leaving, so it can't overwrite another page's panel tests.
 */
onBeforeUnmount(clinicRequests.invalidate);

await fetchClinicDetailBySlug();

/**
 * Open modal with selected service and clinic view.
 */
function openModal() {
  getStartedModalStore.navigate("select_clinic");
  getStartedModalStore.open({
    title: "",
    slug: "",
    clinic_view: "map",
    ...selectedService.value,
    content: "",
  });
}

/**
 * Abort navigation and reset service param.
 */
async function abort() {
  navigateTo({
    params: {
      ...route.params,
      service: "",
    },
  });
}

/**
 * Watch for modal open state changes to trigger abort if closed.
 */
watch(
  () => getStartedModalStore.isOpen,
  (isOpen, wasOpen) => {
    if (wasOpen && !isOpen) abort();
  }
);

/**
 * Oen/Close modal on component mount/unmount.
 */
onBeforeUnmount(getStartedModalStore.close);
onBeforeMount(openModal);
</script>
